import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import Layout from '../components/Layout';
import { FileText, Calendar, Users } from 'lucide-react';
import { format } from 'date-fns';

export default function UploadHistory({ user }) {
  const [scorecards, setScorecards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchScorecards();
  }, []);

  const fetchScorecards = async () => {
    try {
      const response = await axios.get(`${API}/scorecards`);
      setScorecards(response.data);
    } catch (error) {
      console.error('Failed to fetch scorecards:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center min-h-screen">
          <p className="font-barlow text-xl text-slate-900">Loading history...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="max-w-7xl mx-auto p-4 md:p-8 lg:p-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-barlow font-bold text-4xl md:text-5xl text-slate-900 uppercase tracking-tight mb-2">
            Upload History
          </h1>
          <p className="font-inter text-stone-600">View all your uploaded scorecards</p>
        </div>

        {/* History List */}
        {scorecards.length === 0 ? (
          <div className="bg-white border border-stone-200 rounded-sm p-12 text-center shadow-sm">
            <FileText className="w-16 h-16 text-stone-300 mx-auto mb-4" strokeWidth={1.5} />
            <p className="font-barlow text-xl text-slate-900 mb-2">No uploads yet</p>
            <p className="font-inter text-stone-600">Upload your first scorecard to get started</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {scorecards.map(scorecard => (
              <div 
                key={scorecard.id} 
                data-testid="scorecard-item"
                className="bg-white border border-stone-200 rounded-sm p-6 shadow-sm hover:border-amber-600/50 transition-colors duration-300"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="bg-stone-100 p-3 rounded-sm">
                      <FileText className="w-6 h-6 text-amber-600" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-barlow font-semibold text-lg text-slate-900 mb-1">
                        {scorecard.file_name}
                      </h3>
                      <div className="flex items-center gap-4 text-sm text-stone-600">
                        <div className="flex items-center gap-1 font-inter">
                          <Calendar className="w-4 h-4" strokeWidth={1.5} />
                          <span>
                            {scorecard.scorecard_date 
                              ? `Match: ${format(new Date(scorecard.scorecard_date), 'MMM dd, yyyy')}`
                              : `Uploaded: ${format(new Date(scorecard.upload_date), 'MMM dd, yyyy')}`
                            }
                          </span>
                        </div>
                        <div className="flex items-center gap-1 font-inter">
                          <Users className="w-4 h-4" strokeWidth={1.5} />
                          <span>{scorecard.players.length} players</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {[...new Set(scorecard.players.map(p => p.division))].map(div => (
                      <span 
                        key={div}
                        className="inline-block bg-stone-100 text-slate-900 font-barlow font-semibold text-xs px-3 py-1 rounded-sm uppercase tracking-wider"
                      >
                        Div {div}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}